import { Alert, AlertColor, AlertPropsColorOverrides, Box, Button, CircularProgress } from '@mui/material';
import { OverridableStringUnion } from '@mui/types';
import TextSkeletonLoader from 'components/apiHandling/TextSkeletonLoader';
import AIGradientButton from 'components/design/AI/AIGradientButton';
import AIGradientText from 'components/design/AI/AIGradientText';
import React, { useEffect, useState } from 'react';
import * as Sentry from "@sentry/react";
import { useTranslation } from 'react-i18next';
import { AIAsyncResponse, AIError, AIMetadata, AIOutput, AIResponseType } from 'system/AIResponseTypes';
import APIRequest from 'system/APIRequest';
import { permissionsTest } from 'system/InfegyUser';

export interface AILoaderProps {
  queryIds?: string[];
  queryBody?: any;

  responseType: AIResponseType;

  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;

  isStreaming: boolean;
  setIsStreaming: (isStreaming: boolean) => void;

  metadata?: AIMetadata;
  setMetadata: (metadata: AIMetadata | undefined) => void;

  data?: AIOutput;
  setData: (data: AIOutput | undefined) => void;

  saveToWidgetId?: string;
  canSaveWidgetCache?: boolean;

  showRerunButton?: boolean;

  children?: React.ReactNode;

  autoLoad?: boolean;
}

export function AILoader(props: AILoaderProps) {
  const { i18n } = useTranslation();

  const {
    queryIds, responseType,
    metadata, setMetadata,
    isLoading, setIsLoading,
    isStreaming, setIsStreaming,
    saveToWidgetId, queryBody,
    showRerunButton, canSaveWidgetCache,
    data, setData,
    autoLoad
   } = props;

  // Polling timeout so we can cancel it when the component unmounts
  const timeoutRef = React.useRef(null);

  const [needsLoad, setNeedsLoad] = useState<boolean>(!!autoLoad);
  const [needsPoll, setNeedsPoll] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string|undefined>(undefined);
  const [errorSeverity, setErrorSeverity] = useState<OverridableStringUnion<AlertColor, AlertPropsColorOverrides> | undefined>(undefined);

  const [notFoundRetries, setNotFoundRetries] = useState<number>(0);
  const maxRetries = 1;

  const [lastResponseType, setLastResponseType] = useState<AIResponseType | undefined>(undefined);
  const [lastQueryIds, setLastQueryIds] = useState<string[] | undefined>(undefined);
  const [lastQueryBody, setLastQueryBody] = useState<any>(undefined);


  function resetComponent() {
    setMetadata(undefined);
    setData(undefined);
    setIsStreaming(false);
    setNeedsPoll(false);
    setNeedsLoad(!!props.autoLoad);
    setIsLoading(false);
    setErrorMsg(undefined);
    setErrorSeverity(undefined);
    setLastQueryBody(undefined);
    setLastResponseType(undefined);
    setLastQueryIds(undefined);
  }

   // If we have new queryData, we need to reset the loader
  useEffect(() => {
    // We've never run a query, so we don't need to reset
    if (!lastResponseType && !lastQueryIds && !lastQueryBody) {
      return;
    }

    const queryIdsMatch = queryIds?.length === lastQueryIds?.length && queryIds?.every((id, index) => id === lastQueryIds?.[index]);
    if (!queryIdsMatch || queryBody !== lastQueryBody || responseType !== lastResponseType) {
      resetComponent();
    }
  }, [queryIds, queryBody, responseType]);

    // Fetch initial data (metadata, our token for streaming, or possibly an error)
  useEffect(() => {
    if ((!queryIds?.length && !queryBody) || isLoading || metadata?.token || !needsLoad) {
      return;
    }

    // Don't run if we're already loading the same query and response type
    const queryIdsMatch = queryIds?.length === lastQueryIds?.length && queryIds?.every((id, index) => id === lastQueryIds?.[index]);
    if (queryIdsMatch || (queryBody && queryBody === lastQueryBody) || responseType === lastResponseType) {
      return;
    }

    setLastQueryIds(queryIds);
    setLastQueryBody(queryBody || {});
    setLastResponseType(responseType);

    setMetadata(undefined);
    setData(undefined);
    setIsLoading(true);
    setNeedsLoad(false);
    setErrorMsg(undefined);
    setErrorSeverity(undefined);

    const requestOpts: any = {};

    if (saveToWidgetId && canSaveWidgetCache) {
      requestOpts['save_to_widget'] = saveToWidgetId;
    }

    let endpoint = 'query/ai-summary-structured';
    if (responseType === AIResponseType.PERSONAS) {
      endpoint = 'query/ai-social-personas-structured';
    }

    let localQueryBody = queryBody;
    if (localQueryBody ?? typeof localQueryBody !== 'string') {
      localQueryBody = JSON.stringify(localQueryBody);
    }
    if (!localQueryBody) {
      localQueryBody = "{}";
    }

    let URL = `${endpoint}`;
    if (queryIds) {
      URL += `/${queryIds.join(",")}`;
    }

    APIRequest(URL, requestOpts, {
      method: "POST",
      "body": localQueryBody,
    },
    // true below skips caching for this request
    true).then((response: AIMetadata | AIError) => {
      setIsLoading(false);
      if(response?.token) {
          setMetadata(response);
          setNeedsPoll(true);
      } else if(response?.error) {
          setErrorMsg(response.error);
          setErrorSeverity("warning");
      } else {
        setErrorMsg("No response");
        setErrorSeverity("warning");
      }
    }).catch((reason: any | null | undefined) => {
      setIsLoading(false);
      if(reason) {
          setErrorMsg(reason?.server_message || reason?.message || reason?.error || reason?.body?.error || "API request failed");
          setErrorSeverity(reason.code >= 400 && reason.code < 500 ? "warning" : "error");
      } else {
        setErrorMsg("API request failed");
        setErrorSeverity("error");
      }
    });

  }, [queryIds, queryBody, metadata?.token, responseType, isLoading, needsLoad]);

  // Poll for new data, once we have a token
  const pollData = () => {
    if (!metadata || !metadata?.token || data?.complete || errorMsg) {
      setIsStreaming(false);
      setNeedsPoll(false);
      return;
    }
    setIsStreaming(true);

    // Below, we pass undefined placeholder args to be able to pass bypassCache: true
    APIRequest(`ai-async/${metadata.token}`, undefined, undefined, true).then((response: AIAsyncResponse) => {
      if(response?.error) {
        setErrorMsg(response.error);
        setErrorSeverity("warning");
        setIsStreaming(false);
      } else if(response.output) {
        const summary = { complete: response.complete, ...response.output};
        setData(summary);
        if (!response.complete) {
          setNeedsPoll(true);
        } else {
          // Done!
          setIsStreaming(false);
        }
      } else {
        console.error(`No async response: ${response}`);
        Sentry.captureMessage(`No async response: ${response}`);
        setErrorMsg("No async response!");
        setErrorSeverity("error");
        setIsStreaming(false);
      }
    }).catch((reason: any | null | undefined) => {
      setData(undefined);
      setIsStreaming(false);
      if(reason) {
          // 404 means the token wasn't found, possibly expired.
          if(reason.code == 404) {
            // We can retry from the beginning, generating a new token
            if (notFoundRetries < maxRetries) {
              console.debug(`AI async token not found, retrying... (${notFoundRetries + 1}/${maxRetries})`);
              setNotFoundRetries(notFoundRetries + 1);
              setIsLoading(false);
              setNeedsLoad(true);
              setNeedsPoll(false);
              setIsStreaming(false);
              setErrorMsg(undefined);
              setErrorSeverity(undefined);
            } else {
              setErrorMsg(i18n.t("widget-types.ai-summary.errors.expired"));
              setErrorSeverity("info");
            }
            setMetadata(undefined);
            setData(undefined);
          } else {
            setErrorMsg(reason?.body?.error || reason?._message || "API request failed");
            setErrorSeverity(reason.code >= 400 && reason.code < 500 ? "warning" : "error");
          }
      } else {
        setErrorMsg("Async failed!");
        setErrorSeverity("error");
      }
    });
  };

  useEffect(() => {
    if (metadata?.token && !data && !needsPoll && !timeoutRef.current) {
      setNeedsPoll(true);
    }
  }, [metadata, metadata?.token, data]);

  useEffect(() => {
    if (metadata && metadata?.token && needsPoll ) {
      setIsStreaming(true);
      // Poll every 250 ms
      timeoutRef.current = setTimeout(() => {
        pollData();
      }, 250);
      setNeedsPoll(false);
    }
  }, [metadata, metadata?.token, needsPoll]);

  // If we're loading, output the loader
  if(isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: "100%", pt: 2 }}>
        <AIGradientText>{i18n.t("analyzing")}...</AIGradientText>
        <TextSkeletonLoader />
      </Box>
    );
  }

  // If we have no metadata, and we're not already loading, we output a button to initiate loading
  // Or, if the user lacks permissions to run queries, we output a message
  if(errorMsg || (metadata === undefined && !isLoading && !needsLoad && !isStreaming)) {
    // Check if the user has permission to run queries, can use ai features, and can modify the widget (to update the cache)
    if(!permissionsTest(['run_query', 'ai_features'])) {
      return (
        <Alert severity={"info"}>
          {i18n.t("widget-types.ai-summary.errors.not-generated")}<br />
        </Alert>
      );
    }

    return (
      <Box sx={{ m:3 }}>
        {errorMsg &&
          <Alert severity={errorSeverity || "error"} sx={{ mb: 2 }}>
            {errorMsg}<br />
          </Alert>
        }
        <Box sx={{ textAlign: "center" }}>
          <AIGradientButton
            onClick={() => {
              setMetadata(undefined);
              setData(undefined);
              setErrorMsg(undefined);
              setErrorSeverity(undefined);
              setLastQueryBody(undefined);
              setLastResponseType(undefined);
              setLastQueryIds(undefined);
              setNeedsLoad(true);
            }}
            disabled={isLoading ?? !queryIds}>
            {isLoading ?? !queryIds ? i18n.t("loading") : i18n.t("widget-types.ai-summary.run-ai-summary")}
          </AIGradientButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
    {errorMsg && <Alert severity={errorSeverity}>{errorMsg}</Alert>}
    <Box>
      {props.children}
    </Box>
    {isStreaming && (
      <Box sx={{ width: "100%", pt: 2, textAlign: "center" }}>
        <CircularProgress />
        <AIGradientText>{i18n.t("generating")}...</AIGradientText>
      </Box>
    )}
    {data?.complete && !isStreaming && showRerunButton &&
      <Button 
        className="no-print"
        onClick={() => {
          setMetadata(undefined);
          setData(undefined);
          setErrorMsg(undefined);
          setErrorSeverity(undefined);
          setLastQueryBody(undefined);
          setLastResponseType(undefined);
          setLastQueryIds(undefined);
          setNeedsLoad(true);
        }}
      >
        {i18n.t("widget-types.ai-summary.re-run-ai-summary")}
      </Button>
    }
    </Box>
  );
}